import { useEffect } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { Button, Card, Spinner } from '@timely/ui';
import { useWorkspaces, ActiveWorkspaceProvider } from './WorkspaceProvider';
import { resolveWorkspace } from './resolve';
import { ChooseCompany } from '../screens/CreateCompany';
import { PausedCompanies } from './PausedCompany';

/**
 * Everything tenant-shaped hangs off here, and nothing renders until the slug
 * has been resolved to a company this person is actually in.
 *
 * The refusal is deliberate in its wording. Somebody asking for a company they
 * do not belong to is told they do not have access — not sent to a 404, which
 * would confirm which company names are real, and not quietly redirected into
 * a company they did not ask for, which is how people end up looking at the
 * wrong customer's balance and believing it.
 *
 * The guard is a convenience, not the boundary. Row Level Security is the
 * boundary: even with this component removed, every query would return nothing.
 */
export function RequireWorkspace() {
    const { slug } = useParams();
    const { contexts, paused, loading, error, lastUsedSlug, rememberSlug, reload } = useWorkspaces();

    const resolution = loading ? null : resolveWorkspace(contexts, slug, lastUsedSlug);

    useEffect(() => {
        if (resolution?.status === 'ok') rememberSlug(resolution.context.workspace.slug);
    }, [resolution, rememberSlug]);

    if (loading) {
        return (
            <div className="min-h-dvh grid place-items-center" role="status" aria-live="polite">
                <span className="sr-only">Loading your company</span>
                <Spinner className="text-accent w-6 h-6" />
            </div>
        );
    }

    if (error) {
        return (
            <main className="min-h-dvh grid place-items-center px-4">
                <Card className="max-w-md w-full px-7 py-8 text-center" role="alert">
                    <h1 className="text-[20px] font-bold text-label tracking-[-0.02em]">
                        We could not load your companies
                    </h1>
                    <p className="text-[14px] text-label-2 mt-3 leading-relaxed">{error}</p>
                    <div className="mt-7">
                        <Button variant="primary" onClick={() => { void reload(); }}>Try again</Button>
                    </div>
                </Card>
            </main>
        );
    }

    // A company this person belongs to that is not in ordinary use: say why,
    // rather than "no access" — and give an owner the way to cancel a deletion.
    const pausedHere = slug ? paused.find(p => p.slug === slug) : undefined;
    if (pausedHere && resolution!.status !== 'ok') {
        return <PausedCompanies workspaces={[pausedHere]} showOthersLink={contexts.length > 0} />;
    }

    switch (resolution!.status) {
        case 'no-workspaces':
            if (paused.length) return <PausedCompanies workspaces={paused} showOthersLink={false} />;
            return <Navigate to="/onboarding/company" replace />;
        case 'redirect':
            return <Navigate to={`/w/${resolution!.slug}/today`} replace />;
        case 'choose':
            return <ChooseCompany contexts={resolution!.contexts} />;
        case 'forbidden':
            return <NoAccess />;
        case 'ok':
            return (
                <ActiveWorkspaceProvider value={resolution!.context}>
                    <Outlet />
                </ActiveWorkspaceProvider>
            );
    }
}

function NoAccess() {
    return (
        <main className="min-h-dvh grid place-items-center px-4">
            <Card className="max-w-md w-full px-7 py-8 text-center" role="alert">
                <h1 className="text-[20px] font-bold text-label tracking-[-0.02em]">
                    You do not have access to this company
                </h1>
                <p className="text-[14px] text-label-2 mt-3 leading-relaxed">
                    If you think you should, ask an administrator there to invite you. An
                    invitation is sent to one address and only that address can accept it.
                </p>
                <div className="mt-7">
                    <Button variant="primary" onClick={() => { window.location.href = '/'; }}>
                        Go to your companies
                    </Button>
                </div>
            </Card>
        </main>
    );
}

/**
 * `/` for somebody who is signed in: send them wherever they belong.
 *
 * Belonging to nothing is an ordinary state, not an error — it is where every
 * new account starts and where somebody sits between being invited and
 * accepting.
 */
export function WorkspaceHomeRedirect() {
    const { contexts, paused, loading, lastUsedSlug } = useWorkspaces();
    if (loading) {
        return (
            <div className="min-h-dvh grid place-items-center" role="status" aria-live="polite">
                <span className="sr-only">Loading your companies</span>
                <Spinner className="text-accent w-6 h-6" />
            </div>
        );
    }
    const resolution = resolveWorkspace(contexts, null, lastUsedSlug);
    if (resolution.status === 'no-workspaces') {
        // Somebody whose only company is scheduled for deletion is not a new
        // user: show them the company and the way back, not the setup screen.
        if (paused.length) return <PausedCompanies workspaces={paused} showOthersLink={false} />;
        return <Navigate to="/onboarding/company" replace />;
    }
    if (resolution.status === 'redirect') return <Navigate to={`/w/${resolution.slug}/today`} replace />;
    if (resolution.status === 'choose') return <ChooseCompany contexts={resolution.contexts} />;
    return <Navigate to="/onboarding/company" replace />;
}
